import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import ElementBackground from '../utils/ElementBackground';
import { useTheme } from '../../contexts/ThemeContext';
import { CHARACTER_SEX, characterRenderSrc } from '../../core/Build';
import { buildLookStringFromVisuals, getClassBoneName, getLookCacheKey } from '../../services/characterLook';
import {
    createCharacterAnimator,
    footLiftForBone,
    getBareRenderReference,
    renderCharacterSnapshot,
    stanceOrderForBone,
} from '../../services/CharacterRenderService';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import Tooltip from '../utils/Tooltip';
import { recordUnmappedRenderSkins } from '../../services/metrics';
import './BuildCharacter.css';

// ==========================
// GLOBAL CONSTANTS
// ==========================
const DEFAULT_POSE = {
    pivotBottom: '5%',   // distance of shadow center from image bottom
    scale: 0.75,          // overall character scale
    lift: '0%'           // fine-tune vertical lift if needed
};

const SHADOW_OPACITY = 1;
const SHADOW_LUMINOSITY_LIGHT = 0.3;  // blend toward white in light mode
const SHADOW_LUMINOSITY_DARK = 0;     // no lightening in dark mode
/** Debounce skin re-renders when equipping several items quickly. */
const SKIN_RENDER_DEBOUNCE_MS = 150;
/** How long the outgoing character takes to fade when the character changes.
 *  Must match the fade-out on `.character-image--animated` in BuildCharacter.css:
 *  the new render is held back this long so the canvas is already transparent
 *  when its contents are replaced, rather than the old character visibly
 *  turning into the new one halfway through the fade. */
const CHARACTER_FADE_OUT_MS = 250;

const TXT_NO_CHARACTER = {
    fr: 'Aucune classe sélectionnée',
    en: 'No character selected',
    de: 'Keine Klasse ausgewählt',
    es: 'Ninguna clase seleccionada',
    pt: 'Nenhuma classe selecionada',
};
const TXT_CHARACTER_ALT = { fr: 'Personnage', en: 'Character', de: 'Charakter', es: 'Personaje', pt: 'Personagem' };
const TXT_TURN_LEFT = { fr: 'Tourner à gauche', en: 'Turn left', de: 'Nach links drehen', es: 'Girar a la izquierda', pt: 'Virar à esquerda' };
const TXT_TURN_RIGHT = { fr: 'Tourner à droite', en: 'Turn right', de: 'Nach rechts drehen', es: 'Girar a la derecha', pt: 'Virar à direita' };
const TXT_STANCE = { fr: 'Changer de pose', en: 'Change stance', de: 'Haltung wechseln', es: 'Cambiar de pose', pt: 'Mudar de pose' };

const lighten = (c, t) => ({
    r: Math.round(c.r + (255 - c.r) * t),
    g: Math.round(c.g + (255 - c.g) * t),
    b: Math.round(c.b + (255 - c.b) * t)
});

// Helper to get the portrait image for a character class
function getClassImage(characterClass, characterSex) {
    if (!characterClass) return '';
    if (characterSex === CHARACTER_SEX.FEMALE && characterClass.img_f) return characterClass.img_f;
    return characterClass.img_m || characterClass.img_f || '';
}

function getPersistedRenderSrc(build) {
    const render = build?.getCharacterRender?.() ?? build?.characterRender ?? null;
    return characterRenderSrc(render);
}

const BuildCharacter = ({
    build,
    enableSkinRender = true,
    onRenderCaptured = null,
    onViewChange = null,
}) => {
    const theme = useTheme();
    const { lang } = useParams();
    const characterClass = build.getCharacterClass();
    const characterSex = build.getCharacterSex?.() ?? build.characterSex ?? CHARACTER_SEX.MALE;
    const image = getClassImage(characterClass, characterSex);
    const className = characterClass?.name || 'default';
    const elementColor = build.getElementTint();
    const cloakItem = build.getCloak?.() ?? build.items?.[1] ?? null;
    const hatItem = build.getHat?.() ?? build.items?.[0] ?? null;
    const cloakSkinId = cloakItem?.render_skin_id ?? null;
    const hatSkinId = hatItem?.render_skin_id ?? null;
    const visualSkinIds = useMemo(() => (
        [cloakSkinId, hatSkinId]
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id) && id > 0)
    ), [cloakSkinId, hatSkinId]);
    const unmappedEquipIds = useMemo(() => {
        const ids = [];
        for (const item of [hatItem, cloakItem]) {
            if (!item?.id) continue;
            const skin = item.render_skin_id;
            if (skin == null || skin === '') ids.push(item.id);
        }
        return ids;
    }, [hatItem?.id, hatItem?.render_skin_id, cloakItem?.id, cloakItem?.render_skin_id]);
    const renderRequest = useMemo(() => {
        if (!enableSkinRender) return null;
        const lookString = buildLookStringFromVisuals({
            className,
            characterSex,
            skinIds: visualSkinIds,
            accentRgb: elementColor,
        });
        return lookString ? { lookString, key: getLookCacheKey(lookString) } : null;
    }, [characterSex, className, elementColor, enableSkinRender, visualSkinIds]);
    const [renderedImage, setRenderedImage] = useState(null);
    const [renderedImageVisible, setRenderedImageVisible] = useState(false);
    const [animationVisible, setAnimationVisible] = useState(false);
    const [animationFailed, setAnimationFailed] = useState(false);
    // Facings the current pose offers, and where we are in them. Driven by the
    // animator rather than assumed: poses differ in how many ways they turn.
    const [facings, setFacings] = useState([]);
    // Which stance the character is shown in. Every class ships all of them, so
    // this only ever picks between files already on disk. Null means "whatever
    // this class leads with", so a class that leads with its idle keeps doing so
    // until the control is actually used.
    const [stanceChoice, setStanceChoice] = useState(null);
    const [facingChoice, setFacingChoice] = useState(null);
    const canvasRef = useRef(null);
    const animatorRef = useRef(null);
    const rootRef = useRef(null);
    const frameRef = useRef(null);
    const bareReferenceRef = useRef(null);
    const fadeOutUntilRef = useRef(0);
    // Identity of the character itself, as opposed to what it is wearing.
    const characterKey = `${className}|${characterSex}`;
    const characterKeyRef = useRef(characterKey);
    // Where the turn controls are mounted. The character lives in
    // `.build-background`, a deliberately non-interactive layer that every part
    // of the slot grid paints over, so a button left in there is visible and
    // unclickable — no z-index inside it can reach past its own stacking
    // context. `.build-slots-center` is the interactive column over the
    // character, and already hosts the sex toggle, which is the same kind of
    // control. Scoped to this build's own section rather than found globally.
    const [turnHost, setTurnHost] = useState(null);
    // The same character with nothing on, which is what the body alone measures.
    // No accent: colours do not move the bounds, and leaving them out keeps one
    // cache entry per class and sex instead of one per element tint.
    const bareLookString = useMemo(() => buildLookStringFromVisuals({
        className,
        characterSex,
        skinIds: [],
    }), [className, characterSex]);
    // The class skeleton, which is what carries the class's own combat stance.
    const boneName = useMemo(() => getClassBoneName(className), [className]);
    const stanceOrder = useMemo(() => stanceOrderForBone(boneName), [boneName]);
    // What the build was last looked at in, if it remembers. Validated against
    // what this class actually offers, so a stance or facing saved before the
    // vocabulary changed falls back rather than rendering nothing.
    const savedStance = build.getCharacterStance?.() ?? null;
    const savedFacing = build.getCharacterFacing?.() ?? null;
    const stance = stanceChoice
        ?? (stanceOrder.includes(savedStance) ? savedStance : stanceOrder[0]);
    const facing = facingChoice ?? savedFacing;
    const footLift = footLiftForBone(boneName, stance);
    const pose = footLift ? { ...DEFAULT_POSE, lift: footLift } : DEFAULT_POSE;
    const [baseImageSrc, setBaseImageSrc] = useState(image);
    const persistedRenderSrc = useMemo(() => getPersistedRenderSrc(build), [build]);

    useEffect(() => {
        if (!enableSkinRender || !unmappedEquipIds.length) return;
        recordUnmappedRenderSkins(unmappedEquipIds);
    }, [enableSkinRender, unmappedEquipIds]);

    useEffect(() => {
        setBaseImageSrc(image);
    }, [image]);

    useEffect(() => {
        if (!enableSkinRender) return;
        const section = rootRef.current?.closest('.build-section');
        setTurnHost(section?.querySelector('.build-slots-center') || null);
    }, [enableSkinRender]);

    // A different class or gender is a different character, so let the old one
    // leave and the new one arrive rather than swapping in place. Keyed on the
    // character rather than on the look, because equipping a hat is the same
    // character and should not blink; and skipped on the first run, where there
    // is nothing to fade out and the delay would only hold up first paint.
    useEffect(() => {
        if (!enableSkinRender || characterKeyRef.current === characterKey) return;
        characterKeyRef.current = characterKey;
        fadeOutUntilRef.current = performance.now() + CHARACTER_FADE_OUT_MS;
        setAnimationVisible(false);
    }, [enableSkinRender, characterKey]);

    useEffect(() => {
        if (enableSkinRender) return;
        setRenderedImage(persistedRenderSrc);
        setRenderedImageVisible(Boolean(persistedRenderSrc));
    }, [enableSkinRender, persistedRenderSrc]);

    useEffect(() => {
        let active = true;
        let revealRaf = null;
        let debounceTimer = null;
        if (!enableSkinRender) return () => { active = false; };
        if (!renderRequest) {
            setRenderedImage(null);
            setRenderedImageVisible(false);
            if (onRenderCaptured) onRenderCaptured(null);
            return () => { active = false; };
        }

        debounceTimer = window.setTimeout(() => {
            if (!active) return;
            renderCharacterSnapshot(renderRequest.lookString, boneName, stance)
                .then((snapshot) => {
                    if (!active || !snapshot?.url) return;
                    setRenderedImage(snapshot.url);
                    revealRaf = window.requestAnimationFrame(() => {
                        if (active) setRenderedImageVisible(true);
                    });
                    if (onRenderCaptured && snapshot?.dataUrl) {
                        onRenderCaptured({
                            key: renderRequest.key,
                            dataUrl: snapshot.dataUrl,
                        });
                    }
                })
                .catch((error) => {
                    console.warn('[character-render] Render failed:', error);
                });
        }, SKIN_RENDER_DEBOUNCE_MS);

        return () => {
            active = false;
            if (debounceTimer != null) {
                window.clearTimeout(debounceTimer);
            }
            if (revealRaf != null) {
                window.cancelAnimationFrame(revealRaf);
            }
        };
    }, [boneName, enableSkinRender, onRenderCaptured, renderRequest, stance]);

    // Equipment and pose both grow the render around the character, so hand the
    // extra height to CSS as a multiplier rather than letting it squash the
    // character into a fixed box. Re-run after anything that changes the bounds,
    // turning included.
    // Written straight to the element rather than through state. These describe
    // a canvas the renderer resized imperatively, and a React commit lands a
    // frame later than the pixels do — which showed as the new character
    // appearing at the previous one's scale and offset before snapping into
    // place. Written here they are in the DOM before anything paints.
    const applyRenderMetrics = useCallback(() => {
        const canvas = canvasRef.current;
        const frame = frameRef.current;
        const reference = bareReferenceRef.current;
        if (!canvas || !frame || !reference) return;
        if (reference.height > 0 && canvas.height > 0) {
            frame.style.setProperty('--render-height-ratio', String(canvas.height / reference.height));
        }
        // Centre the character on its own root rather than on the middle of the
        // canvas. A raised blade or a summon grows the box to one side only, so
        // centring the box walks the feet off the platform by half of whatever
        // the pose is carrying.
        const anchor = animatorRef.current?.anchorX();
        if (Number.isFinite(anchor) && canvas.width > 0) {
            frame.style.setProperty('--render-anchor-x', String(anchor / canvas.width));
        }
        // And stand it on the platform by its own root rather than by the bottom
        // of the box. A stance whose bounds reach further below the feet than
        // the reference pose does would otherwise hover by exactly that much —
        // a Sacrieur by 67px, a Cra by 30, a Huppermage by none.
        const anchorY = animatorRef.current?.anchorY();
        if (Number.isFinite(anchorY) && canvas.height > 0) {
            const belowRoot = canvas.height - anchorY;
            frame.style.setProperty(
                '--render-anchor-y',
                String((belowRoot - reference.belowRoot) / canvas.height),
            );
        }
        // The background clips, and the tallest poses now outgrow it — a
        // Huppermage's blade turned away reaches half again the character's own
        // height. Cap the render at the room above its feet so it shrinks to fit
        // instead of losing its tip. Measured, because the room depends on a
        // layout this component cannot see, and converted through the frame's
        // own transform so the cap is in the units `height` is written in.
        const clip = canvas.closest('.build-background');
        if (!clip) return;
        const box = canvas.getBoundingClientRect();
        const cssHeight = parseFloat(getComputedStyle(canvas).height);
        const transform = cssHeight > 0 ? box.height / cssHeight : 1;
        const room = box.bottom - clip.getBoundingClientRect().top;
        if (transform > 0 && room > 0) {
            frame.style.setProperty('--render-max-height', `${Math.floor(room / transform)}px`);
        }
    }, []);

    // Back to what the CSS falls back to. A failed render leaves these holding
    // the last successful one's numbers, which is another character's scale and
    // offset applied to whatever is on screen.
    const resetRenderMetrics = useCallback(() => {
        const frame = frameRef.current;
        if (!frame) return;
        for (const name of ['--render-height-ratio', '--render-anchor-x', '--render-anchor-y', '--render-max-height']) {
            frame.style.removeProperty(name);
        }
    }, []);

    const turn = useCallback((step) => {
        const animator = animatorRef.current;
        if (!animator) return;
        const available = animator.facings();
        if (available.length < 2) return;
        const at = available.indexOf(animator.facing());
        const next = available[(at + step + available.length) % available.length];
        setFacingChoice(next);
        onViewChange?.({ stance, facing: next });
        animator.face(next).then(applyRenderMetrics);
    }, [applyRenderMetrics, onViewChange, stance]);

    // The canvas plays the same pose the snapshot above freezes, and sits over
    // it: the still paints first and stays put if the loop never starts, so a
    // renderer failure costs the animation rather than the character.
    //
    // Once the loop is up the still must go dark — it is frozen on frame 0
    // while the canvas moves off it, and left lit it reads as a ghost behind
    // the character. It stays mounted so it can light again if the loop stops.
    useEffect(() => {
        if (!enableSkinRender) return undefined;
        const canvas = canvasRef.current;
        if (!canvas || !renderRequest) {
            setAnimationVisible(false);
            return undefined;
        }
        setAnimationFailed(false);
        let active = true;
        const debounceTimer = window.setTimeout(() => {
            (async () => {
                try {
                    if (!animatorRef.current) {
                        const animator = await createCharacterAnimator(canvas);
                        if (!active) {
                            animator.dispose();
                            return;
                        }
                        animatorRef.current = animator;
                    }
                    // Let the outgoing character finish leaving first. The
                    // canvas is drawn on the moment the render resolves, so
                    // without this the swap happens while it is still partly
                    // visible. The debounce above already covers most of it.
                    const fadeLeft = fadeOutUntilRef.current - performance.now();
                    if (fadeLeft > 0) {
                        await new Promise((resolve) => { window.setTimeout(resolve, fadeLeft); });
                        if (!active) return;
                    }
                    // Measured alongside the render rather than after it: on a
                    // class change it is a cache miss and its own small render,
                    // which is exactly the delay that let the canvas paint
                    // before its metrics were known.
                    const [reference] = await Promise.all([
                        getBareRenderReference(bareLookString),
                        animatorRef.current.show(renderRequest.lookString, boneName, stance, facing),
                    ]);
                    if (!active) return;
                    bareReferenceRef.current = reference;
                    applyRenderMetrics();
                    setFacings(animatorRef.current.facings());
                    setAnimationVisible(true);
                } catch (error) {
                    console.warn('[character-render] Animation failed:', error);
                    if (!active) return;
                    setAnimationFailed(true);
                    resetRenderMetrics();
                }
            })();
        }, SKIN_RENDER_DEBOUNCE_MS);

        return () => {
            active = false;
            window.clearTimeout(debounceTimer);
        };
    }, [applyRenderMetrics, bareLookString, boneName, enableSkinRender, facing, renderRequest, resetRenderMetrics, stance]);

    useEffect(() => () => {
        animatorRef.current?.dispose();
        animatorRef.current = null;
    }, []);

    // An idle loop nobody is looking at is just battery. Park it when the tab is
    // hidden or the character has scrolled out of view.
    useEffect(() => {
        if (!enableSkinRender) return undefined;
        const canvas = canvasRef.current;
        if (!canvas || typeof IntersectionObserver !== 'function') return undefined;
        let onScreen = true;
        const sync = () => {
            const animator = animatorRef.current;
            if (!animator) return;
            if (onScreen && !document.hidden) animator.resume();
            else animator.pause();
        };
        const observer = new IntersectionObserver(([entry]) => {
            onScreen = entry.isIntersecting;
            sync();
        });
        observer.observe(canvas);
        document.addEventListener('visibilitychange', sync);
        return () => {
            observer.disconnect();
            document.removeEventListener('visibilitychange', sync);
        };
    }, [enableSkinRender]);

    // Calculate element color for drop shadow (lightened toward white)
    const lum = theme === 'light' ? SHADOW_LUMINOSITY_LIGHT : SHADOW_LUMINOSITY_DARK;
    const shadowRgb = lighten(elementColor, lum);
    const shadowColor = `rgba(${shadowRgb.r}, ${shadowRgb.g}, ${shadowRgb.b}, ${SHADOW_OPACITY})`;

    const frameStyleVars = {
        '--foot-pivot-bottom': pose.pivotBottom,
        '--char-scale': pose.scale,
        '--foot-lift': pose.lift
    };
    const characterStyleVars = {
        '--element-shadow-color': shadowColor
    };
    // Either render covers the class portrait, so both have to retire it — the
    // animation can be up before the snapshot lands, and a lit portrait behind a
    // character that has moved off it is the same ghost the still made.
    // What the character area shows, in priority: the animated canvas, then the
    // still, then the class portrait. On the live path the first two lower rungs
    // are fallbacks only — showing them while the canvas loads flashed a
    // placeholder, and the still would flash at the wrong size and offset
    // besides, since both come from measuring the canvas that is not up yet.
    const stillShowing = enableSkinRender
        ? animationFailed && renderedImageVisible
        : renderedImageVisible;
    const placeholderShowing = enableSkinRender
        ? animationFailed && !renderedImageVisible
        : !renderedImageVisible;
    const canTurn = enableSkinRender && facings.length > 1;
    const canSwitchStance = enableSkinRender && stanceOrder.length > 1;
    const stanceLabel = TXT_STANCE[lang] || TXT_STANCE.en;
    const turnLeftLabel = TXT_TURN_LEFT[lang] || TXT_TURN_LEFT.en;
    const turnRightLabel = TXT_TURN_RIGHT[lang] || TXT_TURN_RIGHT.en;

    const characterControls = (canTurn || canSwitchStance) ? (
        <div className="character-controls">
            {canTurn ? (
                <Tooltip content={turnLeftLabel} wrapperClassName="character-control">
                    <button
                        type="button"
                        className="character-control-btn"
                        onClick={() => turn(1)}
                        aria-label={turnLeftLabel}
                    >
                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M15 4 7 12l8 8" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </Tooltip>
            ) : null}
            {canSwitchStance ? (
                <Tooltip content={stanceLabel} wrapperClassName="character-control">
                    <button
                        type="button"
                        className={`character-control-btn${stance === stanceOrder[0] ? '' : ' character-control-btn--on'}`}
                        onClick={() => {
                            const next = stanceOrder[(stanceOrder.indexOf(stance) + 1) % stanceOrder.length];
                            setStanceChoice(next);
                            onViewChange?.({ stance: next, facing: animatorRef.current?.facing() ?? facing });
                        }}
                        aria-label={stanceLabel}
                    >
                        <AccessibilityIcon fontSize="inherit" aria-hidden="true" />
                    </button>
                </Tooltip>
            ) : null}
            {canTurn ? (
                <Tooltip content={turnRightLabel} wrapperClassName="character-control">
                    <button
                        type="button"
                        className="character-control-btn"
                        onClick={() => turn(-1)}
                        aria-label={turnRightLabel}
                    >
                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M9 4l8 8-8 8" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </Tooltip>
            ) : null}
        </div>
    ) : null;

    return (
        <div className="build-character column" style={characterStyleVars} ref={rootRef}>
            {characterControls && turnHost
                ? createPortal(characterControls, turnHost)
                : characterControls}
            {image ? (
                <div className="character-frame" style={frameStyleVars} ref={frameRef}>
                    {/* Color overlay masked by character alpha */}
                    <div
                        className={`character-color-overlay ${placeholderShowing ? '' : 'character-color-overlay--hidden'}`}
                        style={{
                            '--char-image': `url(${baseImageSrc})`
                        }}
                    >
                        <ElementBackground build={build} flatColor="start" />
                    </div>
                    <img
                        src={baseImageSrc}
                        className={`character-image character-image--base ${placeholderShowing ? '' : 'character-image--hidden'}`}
                        alt={characterClass?.name || (TXT_CHARACTER_ALT[lang] || TXT_CHARACTER_ALT.en)}
                        fetchpriority="high"
                        decoding="async"
                        onError={() => {
                            const fallback = characterClass?.img_m || '';
                            if (fallback && fallback !== baseImageSrc) {
                                setBaseImageSrc(fallback);
                            }
                        }}
                    />
                    {renderedImage ? (
                        <img
                            src={renderedImage}
                            className={`character-image character-image--rendered ${stillShowing ? 'character-image--visible' : ''}`}
                            alt=""
                            aria-hidden="true"
                            decoding="async"
                        />
                    ) : null}
                    {enableSkinRender ? (
                        <canvas
                            ref={canvasRef}
                            className={`character-image character-image--rendered character-image--animated ${animationVisible ? 'character-image--visible' : ''}`}
                            aria-hidden="true"
                        />
                    ) : null}
                </div>
            ) : (
                <div>{TXT_NO_CHARACTER[lang] || TXT_NO_CHARACTER.en}</div>
            )}
        </div>
    );
};

BuildCharacter.propTypes = {
    build: PropTypes.object.isRequired,
    enableSkinRender: PropTypes.bool,
    onRenderCaptured: PropTypes.func,
    onViewChange: PropTypes.func,
};

export default BuildCharacter; 
